import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Event as EventIcon,
} from "@material-ui/icons";
import { format, parse, addMinutes, isBefore, isAfter } from "date-fns";
import firebase from "../firebase";

const TherapistSchedule = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [kids, setKids] = useState([]);
  const [workingHours, setWorkingHours] = useState({
    Monday: { enabled: true, start: "09:00", end: "17:00" },
    Tuesday: { enabled: true, start: "09:00", end: "17:00" },
    Wednesday: { enabled: true, start: "09:00", end: "17:00" },
    Thursday: { enabled: true, start: "09:00", end: "17:00" },
    Friday: { enabled: true, start: "09:00", end: "17:00" },
    Saturday: { enabled: false, start: "09:00", end: "17:00" },
    Sunday: { enabled: false, start: "09:00", end: "17:00" },
  });

  const [breaks, setBreaks] = useState([
    { id: 1, name: "Lunch Break", start: "12:00", end: "13:00", days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"] }
  ]);

  const [clientAvailability, setClientAvailability] = useState({});
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [selectedDay, setSelectedDay] = useState("Monday");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  const [blockedTimeDialogOpen, setBlockedTimeDialogOpen] = useState(false);
  const [currentBreak, setCurrentBreak] = useState({ name: "", start: "", end: "", days: [] });
  const [currentBlockedTime, setCurrentBlockedTime] = useState({ date: format(new Date(), "yyyy-MM-dd"), start: "", end: "", reason: "" });
  const [editingBreakId, setEditingBreakId] = useState(null);
  const [editingBlockedId, setEditingBlockedId] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(60);
  const [dailySchedule, setDailySchedule] = useState([]);
  const [selectedClientForAvailability, setSelectedClientForAvailability] = useState("");
  const [clientAvailabilityDialog, setClientAvailabilityDialog] = useState(false);
  const [tempClientAvailability, setTempClientAvailability] = useState({});

  const db = firebase.firestore();
  const userId = global.config?.uid;

  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  // Check authentication
  useEffect(() => {
    if (global.config.uid === "") {
      window.alert("You are not signed in");
      window.location.replace("/");
      setIsAuthenticated(false);
    }
  }, []);

  // Load kids from Firestore
  useEffect(() => {
    if (!userId) return;

    const unsubscribe = db
      .collection("Main")
      .doc(userId)
      .collection("Kids")
      .onSnapshot((snapshot) => {
        const kidsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setKids(kidsData);
      });

    return () => unsubscribe();
  }, [userId]);

  // Load schedule settings from Firestore
  useEffect(() => {
    if (!userId) return;

    const docRef = db.collection("Main").doc(userId).collection("Settings").doc("scheduleSettings");

    const unsubscribe = docRef.onSnapshot((doc) => {
      if (doc.exists) {
        const data = doc.data();
        if (data.workingHours) setWorkingHours(data.workingHours);
        if (data.breaks) setBreaks(data.breaks);
        if (data.clientAvailability) setClientAvailability(data.clientAvailability);
        if (data.blockedTimes) setBlockedTimes(data.blockedTimes);
        if (data.sessionDuration) setSessionDuration(data.sessionDuration);
      }
    });

    return () => unsubscribe();
  }, [userId]);

  // Save settings to Firestore
  const saveSettings = async () => {
    if (!userId) return;

    try {
      await db
        .collection("Main")
        .doc(userId)
        .collection("Settings")
        .doc("scheduleSettings")
        .set({
          workingHours,
          breaks,
          clientAvailability,
          blockedTimes,
          sessionDuration,
        });
      alert("Schedule settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Error saving settings. Please try again.");
    }
  };

  const handleWorkingHoursChange = (day, field, value) => {
    setWorkingHours({
      ...workingHours,
      [day]: {
        ...workingHours[day],
        [field]: value,
      },
    });
  };

  const addBreak = () => {
    if (!currentBreak.name || !currentBreak.start || !currentBreak.end || currentBreak.days.length === 0) {
      alert("Please fill in all break fields");
      return;
    }

    if (editingBreakId !== null) {
      setBreaks(breaks.map(b => b.id === editingBreakId ? { ...currentBreak, id: editingBreakId } : b));
      setEditingBreakId(null);
    } else {
      setBreaks([...breaks, { ...currentBreak, id: Date.now() }]);
    }

    setCurrentBreak({ name: "", start: "", end: "", days: [] });
    setBreakDialogOpen(false);
  };

  const deleteBreak = (id) => {
    if (window.confirm("Are you sure you want to delete this break?")) {
      setBreaks(breaks.filter((b) => b.id !== id));
    }
  };

  const editBreak = (breakItem) => {
    setCurrentBreak(breakItem);
    setEditingBreakId(breakItem.id);
    setBreakDialogOpen(true);
  };

  const addBlockedTime = () => {
    if (!currentBlockedTime.date || !currentBlockedTime.start || !currentBlockedTime.end) {
      alert("Please fill in date, start time, and end time");
      return;
    }

    if (editingBlockedId !== null) {
      setBlockedTimes(blockedTimes.map(bt => bt.id === editingBlockedId ? { ...currentBlockedTime, id: editingBlockedId } : bt));
      setEditingBlockedId(null);
    } else {
      setBlockedTimes([...blockedTimes, { ...currentBlockedTime, id: Date.now() }]);
    }

    setCurrentBlockedTime({ date: format(new Date(), "yyyy-MM-dd"), start: "", end: "", reason: "" });
    setBlockedTimeDialogOpen(false);
  };

  const deleteBlockedTime = (id) => {
    if (window.confirm("Are you sure you want to delete this blocked time?")) {
      setBlockedTimes(blockedTimes.filter((bt) => bt.id !== id));
    }
  };

  const editBlockedTime = (blockedTime) => {
    setCurrentBlockedTime(blockedTime);
    setEditingBlockedId(blockedTime.id);
    setBlockedTimeDialogOpen(true);
  };

  const openClientAvailabilityDialog = (kidId) => {
    setSelectedClientForAvailability(kidId);
    const defaultAvailability = clientAvailability[kidId] || { sessionsPerWeek: 2 };
    setTempClientAvailability(defaultAvailability);
    setClientAvailabilityDialog(true);
  };

  const saveClientAvailability = () => {
    setClientAvailability({
      ...clientAvailability,
      [selectedClientForAvailability]: tempClientAvailability,
    });
    setClientAvailabilityDialog(false);
    setTempClientAvailability({});
  };

  const toggleClientDay = (day) => {
    const current = tempClientAvailability[day] || { enabled: false, preferredTimes: [] };
    setTempClientAvailability({
      ...tempClientAvailability,
      [day]: {
        ...current,
        enabled: !current.enabled,
      },
    });
  };

  const addPreferredTime = (day, time) => {
    const current = tempClientAvailability[day] || { enabled: true, preferredTimes: [] };
    if (!current.preferredTimes.includes(time)) {
      setTempClientAvailability({
        ...tempClientAvailability,
        [day]: {
          ...current,
          preferredTimes: [...current.preferredTimes, time],
        },
      });
    }
  };

  const removePreferredTime = (day, time) => {
    const current = tempClientAvailability[day];
    setTempClientAvailability({
      ...tempClientAvailability,
      [day]: {
        ...current,
        preferredTimes: current.preferredTimes.filter(t => t !== time),
      },
    });
  };

  const generateDailySchedule = () => {
    const day = selectedDay;
    const schedule = [];

    if (!workingHours[day].enabled) {
      setDailySchedule([]);
      return;
    }

    const startTime = parse(workingHours[day].start, "HH:mm", new Date());
    const endTime = parse(workingHours[day].end, "HH:mm", new Date());

    // Get all time slots
    const slots = [];
    let currentTime = startTime;

    while (isBefore(currentTime, endTime)) {
      const slotEnd = addMinutes(currentTime, sessionDuration);
      if (isAfter(slotEnd, endTime)) break;

      const timeStr = format(currentTime, "HH:mm");
      const slotEndStr = format(slotEnd, "HH:mm");

      // Check if slot overlaps with a break
      let isBreak = false;
      let breakName = "";

      for (const breakItem of breaks) {
        if (breakItem.days.includes(day)) {
          const breakStart = parse(breakItem.start, "HH:mm", new Date());
          const breakEnd = parse(breakItem.end, "HH:mm", new Date());

          if (
            (isBefore(currentTime, breakEnd) && isAfter(slotEnd, breakStart)) ||
            (currentTime.getTime() === breakStart.getTime())
          ) {
            isBreak = true;
            breakName = breakItem.name;
            break;
          }
        }
      }

      if (isBreak) {
        slots.push({ time: timeStr, type: "break", name: breakName });
      } else {
        slots.push({ time: timeStr, type: "available", client: null });
      }

      currentTime = slotEnd;
    }

    // Assign clients based on availability and sessions per week
    const availableKids = kids.filter(kid => {
      const availability = clientAvailability[kid.id];
      return availability && availability[day] && availability[day].enabled;
    });

    // Count how many sessions each kid is already scheduled for this week
    const weeklySessionCounts = {};
    kids.forEach(kid => {
      weeklySessionCounts[kid.id] = 0;
    });

    // Count sessions across all days for the week
    daysOfWeek.forEach(weekDay => {
      if (weekDay === day) return; // Skip current day, we'll add it below

      const dayAvailableKids = kids.filter(kid => {
        const availability = clientAvailability[kid.id];
        return availability && availability[weekDay] && availability[weekDay].enabled;
      });

      dayAvailableKids.forEach(kid => {
        const availability = clientAvailability[kid.id];
        const sessionsPerWeek = availability.sessionsPerWeek || 2;
        // Estimate one session per enabled day (simplified)
        if (weeklySessionCounts[kid.id] < sessionsPerWeek) {
          weeklySessionCounts[kid.id]++;
        }
      });
    });

    // Prioritize kids with preferred times, then by sessions needed
    const kidsNeedingSessions = availableKids.filter(kid => {
      const availability = clientAvailability[kid.id];
      const sessionsPerWeek = availability.sessionsPerWeek || 2;
      return weeklySessionCounts[kid.id] < sessionsPerWeek;
    });

    // Sort by number of sessions still needed (descending)
    kidsNeedingSessions.sort((a, b) => {
      const aNeeded = (clientAvailability[a.id]?.sessionsPerWeek || 2) - weeklySessionCounts[a.id];
      const bNeeded = (clientAvailability[b.id]?.sessionsPerWeek || 2) - weeklySessionCounts[b.id];
      return bNeeded - aNeeded;
    });

    // Assign sessions: prioritize kids with preferred times first
    for (const kid of kidsNeedingSessions) {
      const availability = clientAvailability[kid.id][day];
      const sessionsPerWeek = clientAvailability[kid.id].sessionsPerWeek || 2;
      const sessionsNeeded = sessionsPerWeek - weeklySessionCounts[kid.id];

      if (sessionsNeeded <= 0) continue;

      if (availability.preferredTimes && availability.preferredTimes.length > 0) {
        for (const prefTime of availability.preferredTimes) {
          const slot = slots.find(s => s.time === prefTime && s.type === "available" && !s.client);
          if (slot) {
            slot.client = kid;
            weeklySessionCounts[kid.id]++;
            break;
          }
        }
      } else {
        // Assign to any available slot
        const slot = slots.find(s => s.type === "available" && !s.client);
        if (slot) {
          slot.client = kid;
          weeklySessionCounts[kid.id]++;
        }
      }
    }

    setDailySchedule(slots);
  };

  useEffect(() => {
    generateDailySchedule();
  }, [selectedDay, workingHours, breaks, clientAvailability, kids, sessionDuration]);

  const renderWorkingHours = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Weekly Working Hours
      </Typography>

      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Default Session Duration (minutes)</InputLabel>
          <Select
            value={sessionDuration}
            onChange={(e) => setSessionDuration(e.target.value)}
            label="Default Session Duration (minutes)"
          >
            <MenuItem value={30}>30 minutes</MenuItem>
            <MenuItem value={45}>45 minutes</MenuItem>
            <MenuItem value={60}>60 minutes</MenuItem>
            <MenuItem value={90}>90 minutes</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {daysOfWeek.map((day) => (
        <Card key={day} sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={workingHours[day].enabled}
                      onChange={(e) => handleWorkingHoursChange(day, "enabled", e.target.checked)}
                    />
                  }
                  label={<Typography variant="h6">{day}</Typography>}
                />
              </Grid>
              {workingHours[day].enabled && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      type="time"
                      label="Start Time"
                      value={workingHours[day].start}
                      onChange={(e) => handleWorkingHoursChange(day, "start", e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      type="time"
                      label="End Time"
                      value={workingHours[day].end}
                      onChange={(e) => handleWorkingHoursChange(day, "end", e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Button variant="contained" color="primary" onClick={saveSettings} sx={{ mt: 2 }}>
        Save Working Hours
      </Button>
    </Box>
  );

  const renderBreaks = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5">Breaks & Time Off</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setCurrentBreak({ name: "", start: "", end: "", days: [] });
            setEditingBreakId(null);
            setBreakDialogOpen(true);
          }}
        >
          Add Break
        </Button>
      </Box>

      {breaks.map((breakItem) => (
        <Card key={breakItem.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
              <Box>
                <Typography variant="h6">{breakItem.name}</Typography>
                <Typography color="textSecondary">
                  {breakItem.start} - {breakItem.end}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {breakItem.days.map((day) => (
                    <Chip key={day} label={day} size="small" sx={{ mr: 0.5, mt: 0.5 }} />
                  ))}
                </Box>
              </Box>
              <Box>
                <IconButton onClick={() => editBreak(breakItem)} color="primary">
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => deleteBreak(breakItem.id)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}

      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>Blocked Times</Typography>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => {
          setCurrentBlockedTime({ date: format(new Date(), "yyyy-MM-dd"), start: "", end: "", reason: "" });
          setEditingBlockedId(null);
          setBlockedTimeDialogOpen(true);
        }}
        sx={{ mb: 2 }}
      >
        Add Blocked Time
      </Button>

      {blockedTimes.map((blockedTime) => (
        <Card key={blockedTime.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
              <Box>
                <Typography variant="h6">{blockedTime.date}</Typography>
                <Typography color="textSecondary">
                  {blockedTime.start} - {blockedTime.end}
                </Typography>
                {blockedTime.reason && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Reason: {blockedTime.reason}
                  </Typography>
                )}
              </Box>
              <Box>
                <IconButton onClick={() => editBlockedTime(blockedTime)} color="primary">
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => deleteBlockedTime(blockedTime.id)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}

      <Button variant="contained" color="primary" onClick={saveSettings} sx={{ mt: 2 }}>
        Save Break Settings
      </Button>
    </Box>
  );

  const renderClientAvailability = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Client Availability
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Set which days and times each client is available for sessions
      </Typography>

      {kids.length === 0 ? (
        <Alert severity="info">No clients found. Please add clients first.</Alert>
      ) : (
        kids.map((kid) => (
          <Card key={kid.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography variant="h6">
                    {kid.FirstName} {kid.LastName}
                  </Typography>
                  {clientAvailability[kid.id] && (
                    <>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                        {clientAvailability[kid.id].sessionsPerWeek || 2} sessions per week
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {Object.keys(clientAvailability[kid.id])
                          .filter(day => clientAvailability[kid.id][day]?.enabled)
                          .map(day => (
                            <Chip key={day} label={day} size="small" sx={{ mr: 0.5, mt: 0.5 }} />
                          ))}
                      </Box>
                    </>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => openClientAvailabilityDialog(kid.id)}
                >
                  Set Availability
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))
      )}

      <Button variant="contained" color="primary" onClick={saveSettings} sx={{ mt: 2 }}>
        Save Client Availability
      </Button>
    </Box>
  );

  const renderDailySchedule = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Daily Schedule View
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Select Day</InputLabel>
        <Select
          value={selectedDay}
          onChange={(e) => setSelectedDay(e.target.value)}
          label="Select Day"
        >
          {daysOfWeek.map((day) => (
            <MenuItem key={day} value={day}>
              {day}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {!workingHours[selectedDay].enabled ? (
        <Alert severity="info">No working hours set for {selectedDay}</Alert>
      ) : dailySchedule.length === 0 ? (
        <Alert severity="info">No schedule generated. Please set working hours and client availability.</Alert>
      ) : (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {selectedDay} Schedule ({workingHours[selectedDay].start} - {workingHours[selectedDay].end})
          </Typography>

          {dailySchedule.map((slot, index) => (
            <Card key={index} sx={{ mb: 1, bgcolor: slot.type === "break" ? "#fff3e0" : slot.client ? "#e8f5e9" : "#f5f5f5" }}>
              <CardContent sx={{ py: 1.5 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={3}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <TimeIcon sx={{ mr: 1, fontSize: 20 }} />
                      <Typography variant="body1" fontWeight="bold">
                        {slot.time}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={9}>
                    {slot.type === "break" ? (
                      <Typography variant="body1" fontStyle="italic">
                        {slot.name}
                      </Typography>
                    ) : slot.client ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                        <Typography variant="body1">
                          {slot.client.FirstName} {slot.client.LastName}
                          {slot.client.Location && ` - ${slot.client.Location}`}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body1" color="textSecondary">
                        Available
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );

  // Don't render anything if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ px: 3 }}>
        Therapist Schedule Manager
      </Typography>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tab label="Working Hours" icon={<TimeIcon />} />
        <Tab label="Breaks & Time Off" icon={<EventIcon />} />
        <Tab label="Client Availability" icon={<PersonIcon />} />
        <Tab label="Daily Schedule" icon={<EventIcon />} />
      </Tabs>

      {activeTab === 0 && renderWorkingHours()}
      {activeTab === 1 && renderBreaks()}
      {activeTab === 2 && renderClientAvailability()}
      {activeTab === 3 && renderDailySchedule()}

      {/* Break Dialog */}
      <Dialog open={breakDialogOpen} onClose={() => setBreakDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBreakId ? "Edit Break" : "Add Break"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Break Name"
            value={currentBreak.name}
            onChange={(e) => setCurrentBreak({ ...currentBreak, name: e.target.value })}
            fullWidth
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            type="time"
            label="Start Time"
            value={currentBreak.start}
            onChange={(e) => setCurrentBreak({ ...currentBreak, start: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            type="time"
            label="End Time"
            value={currentBreak.end}
            onChange={(e) => setCurrentBreak({ ...currentBreak, end: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Select Days:
          </Typography>
          <Box>
            {daysOfWeek.map((day) => (
              <Chip
                key={day}
                label={day}
                onClick={() => {
                  const days = currentBreak.days.includes(day)
                    ? currentBreak.days.filter((d) => d !== day)
                    : [...currentBreak.days, day];
                  setCurrentBreak({ ...currentBreak, days });
                }}
                color={currentBreak.days.includes(day) ? "primary" : "default"}
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBreakDialogOpen(false)}>Cancel</Button>
          <Button onClick={addBreak} variant="contained">
            {editingBreakId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blocked Time Dialog */}
      <Dialog open={blockedTimeDialogOpen} onClose={() => setBlockedTimeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBlockedId ? "Edit Blocked Time" : "Add Blocked Time"}</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            label="Date"
            value={currentBlockedTime.date}
            onChange={(e) => setCurrentBlockedTime({ ...currentBlockedTime, date: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            type="time"
            label="Start Time"
            value={currentBlockedTime.start}
            onChange={(e) => setCurrentBlockedTime({ ...currentBlockedTime, start: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            type="time"
            label="End Time"
            value={currentBlockedTime.end}
            onChange={(e) => setCurrentBlockedTime({ ...currentBlockedTime, end: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Reason (Optional)"
            value={currentBlockedTime.reason}
            onChange={(e) => setCurrentBlockedTime({ ...currentBlockedTime, reason: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockedTimeDialogOpen(false)}>Cancel</Button>
          <Button onClick={addBlockedTime} variant="contained">
            {editingBlockedId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Client Availability Dialog */}
      <Dialog open={clientAvailabilityDialog} onClose={() => setClientAvailabilityDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Set Client Availability
          {selectedClientForAvailability && kids.find(k => k.id === selectedClientForAvailability) && (
            <Typography variant="subtitle1" color="textSecondary">
              {kids.find(k => k.id === selectedClientForAvailability).FirstName}{" "}
              {kids.find(k => k.id === selectedClientForAvailability).LastName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 3, mt: 2 }}>
            <InputLabel>Sessions Per Week</InputLabel>
            <Select
              value={tempClientAvailability.sessionsPerWeek || 2}
              onChange={(e) => setTempClientAvailability({ ...tempClientAvailability, sessionsPerWeek: e.target.value })}
              label="Sessions Per Week"
            >
              <MenuItem value={1}>1 session per week</MenuItem>
              <MenuItem value={2}>2 sessions per week</MenuItem>
              <MenuItem value={3}>3 sessions per week</MenuItem>
              <MenuItem value={4}>4 sessions per week</MenuItem>
              <MenuItem value={5}>5 sessions per week</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="h6" sx={{ mb: 2 }}>
            Available Days & Times
          </Typography>

          {daysOfWeek.map((day) => (
            <Card key={day} sx={{ mb: 2 }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={tempClientAvailability[day]?.enabled || false}
                      onChange={() => toggleClientDay(day)}
                    />
                  }
                  label={<Typography variant="h6">{day}</Typography>}
                />

                {tempClientAvailability[day]?.enabled && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Preferred Times (Optional):
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <TextField
                        type="time"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            addPreferredTime(day, e.target.value);
                            e.target.value = "";
                          }
                        }}
                      />
                      <Typography variant="caption" color="textSecondary">
                        Press Enter to add
                      </Typography>
                    </Box>
                    <Box>
                      {(tempClientAvailability[day]?.preferredTimes || []).map((time, idx) => (
                        <Chip
                          key={idx}
                          label={time}
                          onDelete={() => removePreferredTime(day, time)}
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientAvailabilityDialog(false)}>Cancel</Button>
          <Button onClick={saveClientAvailability} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TherapistSchedule;
